import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle, Loader2 } from 'lucide-react';
import {
  linkMeasurementToOrder,
  listMeasurementAttachments,
  patchMeasurementReport,
  uploadMeasurementAttachment,
  type CrmOrderBoardCard,
} from '../../../api';
import { formatAdminPanelError } from '@/lib/adminApiError';

type MeasurementRow = CrmOrderBoardCard['measurements'][0];

type Props = {
  measurement: MeasurementRow;
  orderId?: number;
  canEdit: boolean;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  onUpdated: () => void;
};

export function CrmMeasurementReportBlock({
  measurement,
  orderId,
  canEdit,
  hapticSelection,
  hapticSuccess,
  onUpdated,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<CrmOrderBoardCard['attachments']>([]);
  const [reportNotes, setReportNotes] = useState(measurement.report_notes || '');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const items = await listMeasurementAttachments(measurement.id);
      setAttachments(items.filter((a) => a.measurement_id === measurement.id));
    } catch {
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [measurement.id]);

  const onPickPhoto = () => {
    if (!canEdit) return;
    hapticSelection();
    fileRef.current?.click();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const att = await uploadMeasurementAttachment(measurement.id, file);
      setAttachments((prev) => [att, ...prev]);
      hapticSuccess();
      onUpdated();
    } catch (err: unknown) {
      setError(formatAdminPanelError(err, 'Не удалось загрузить фото'));
    } finally {
      setUploading(false);
    }
  };

  const saveReport = async (markComplete: boolean) => {
    setError(null);
    try {
      await patchMeasurementReport(measurement.id, {
        report_notes: reportNotes,
        mark_complete: markComplete,
        order_id: orderId,
      });
      hapticSuccess();
      onUpdated();
    } catch (err: unknown) {
      setError(formatAdminPanelError(err, 'Не удалось сохранить отчёт'));
    }
  };

  useEffect(() => {
    void loadAttachments();
  }, [loadAttachments]);

  useEffect(() => {
    if (orderId && measurement.order_id !== orderId) {
      void linkMeasurementToOrder(measurement.id, orderId).catch(() => undefined);
    }
  }, [measurement.id, measurement.order_id, orderId]);

  const isDone = measurement.status === 'completed';

  return (
    <div
      className="rounded-xl p-3 space-y-3"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-bold" style={{ color: 'var(--text-1)' }}>
            Замер #{measurement.id}
          </p>
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-3)' }}>
            {[measurement.city, measurement.phone, measurement.preferred_time].filter(Boolean).join(' · ')}
          </p>
        </div>
        {measurement.assigned_measurer && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0"
            style={{ background: 'rgba(72,187,120,0.15)', color: '#68D391', border: '1px solid rgba(72,187,120,0.35)' }}>
            {measurement.assigned_measurer.name}
          </span>
        )}
      </div>

      {canEdit && (
        <>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
          <button
            type="button"
            disabled={uploading}
            onClick={onPickPhoto}
            className="press w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold disabled:opacity-50 min-h-[44px]"
            style={{ background: 'rgba(99,179,237,0.15)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            {uploading ? 'Загрузка…' : 'Добавить фото с объекта'}
          </button>
        </>
      )}

      {loading && (
        <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>Загружаем фото…</p>
      )}

      {attachments.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {attachments.map((a) => (
            <a
              key={a.id}
              href={a.file_url}
              target="_blank"
              rel="noreferrer"
              className="block aspect-square rounded-lg overflow-hidden"
              style={{ border: '1px solid var(--border-glass)' }}
            >
              <img src={a.file_url} alt={a.caption || 'фото замера'} className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}

      {canEdit && (
        <textarea
          rows={3}
          value={reportNotes}
          onChange={(e) => setReportNotes(e.target.value)}
          placeholder="Описание объекта: проёмы, нюансы монтажа, рекомендации для цеха…"
          className="input-glass w-full text-xs rounded-xl px-3 py-2 resize-none"
        />
      )}

      {!canEdit && reportNotes && (
        <p className="text-xs leading-relaxed" style={{ color: 'var(--text-2)' }}>{reportNotes}</p>
      )}

      {canEdit && !isDone && (
        <button
          type="button"
          onClick={() => { hapticSelection(); void saveReport(true); }}
          className="press w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold min-h-[44px]"
          style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)' }}
        >
          <CheckCircle size={14} /> Завершить замер → «Отчёт замера»
        </button>
      )}

      {isDone && (
        <p className="text-[10px] font-semibold" style={{ color: '#68D391' }}>
          Отчёт сдан · карточку можно двигать в «Производство»
        </p>
      )}

      {error && (
        <p className="text-[10px]" style={{ color: '#FC814A' }}>{error}</p>
      )}
    </div>
  );
}
